import React, { useRef, useEffect, useState } from 'react';
import { BaseButton } from '@repo/common';
import { twMerge } from 'tailwind-merge';

import { ReactComponent as CheckIcon } from '@repo/common/assets/icons/16px/icon_16px_check.svg';

function FilterList({
  onSelect,
  onClose,
  selectedOption,
  options = [],
  className = '',
  manageOutsideClick = true,
}) {
  const fallbackOptions = [
    'All time',
    'Last 1 month',
    'Last 3 month',
    'Last 4 month',
  ];
  const normalizedOptions =
    options.length > 0
      ? options
          .map((option) => {
            if (typeof option === 'string') {
              return { label: option, value: option };
            }

            const label = option.optionLabel ?? option.label;
            const value = Object.prototype.hasOwnProperty.call(
              option,
              'optionValue',
            )
              ? option.optionValue
              : Object.prototype.hasOwnProperty.call(option, 'value')
                ? option.value
                : label;
            return label ? { label, value } : null;
          })
          .filter(Boolean)
      : fallbackOptions.map((option) => ({ label: option, value: option }));

  const listRef = useRef(null);
  const selectedRef = useRef(null);
  const [localSelected, setLocalSelected] = useState(selectedOption);

  useEffect(() => {
    if (listRef.current && selectedRef.current) {
      listRef.current.scrollTop = selectedRef.current.offsetTop - 8;
    }
  }, [selectedOption]);

  useEffect(() => {
    setLocalSelected(selectedOption);
  }, [selectedOption]);

  useEffect(() => {
    if (!manageOutsideClick) return undefined;

    const handleClickOutside = (e) => {
      if (listRef.current && !listRef.current.contains(e.target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [manageOutsideClick, onClose]);

  return (
    <div
      ref={listRef}
      className={twMerge(
        'w-[160px] h-[172px] overflow-y-auto bg-grayscale-gray00 border border-grayscale-gray02 rounded-lg p-2 shadow-popup-02',
        className,
      )}
    >
      <ul role='menu' className='p-0 m-0 list-none'>
        {normalizedOptions.map(({ label, value }, index) => {
          const isSelected = value === localSelected;

          return (
            <div
              key={`${label}-${index}`}
              ref={isSelected ? selectedRef : null}
              className='flex items-center transition-colors duration-150 rounded-lg'
            >
              <BaseButton
                type='text'
                size='m'
                bgclass='bg-grayscale-gray00'
                borderclass='border-none'
                textclass='justify-start'
                hoverclass='hover:bg-grayscale-gray01 hover:text-text-primary'
                activeclass='active:bg-grayscale-gray02 active:text-text-primary'
                width='100%'
                onMouseDown={() => {
                  setLocalSelected(value);
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setLocalSelected(value);
                  onSelect(value);
                }}
                leftalign='true'
                icon={
                  isSelected ? (
                    <CheckIcon className='text-icon-primary' />
                  ) : (
                    <span className='w-4 h-[18px]' />
                  )
                }
              >
                {label}
              </BaseButton>
            </div>
          );
        })}
      </ul>
    </div>
  );
}

export default FilterList;
